<template>
  <v-container>
    <v-card class="mx-auto pa-4">
      <v-row>
        <v-col cols="3" class="display-1 text--primary">
          <v-icon>camera_alt</v-icon>
          Snapshots
        </v-col>
        <v-col cols="6" class="text--primary text-center">
          Latest
          <span v-for="marketplace in marketplaces" :key="marketplace.id">
            <v-btn :href="'/snapshot/latest/' + marketplace.name + '/' + catCodeString" color="secondary" class="mx-1">
              {{ marketplace.name }}
            </v-btn>
          </span>
        </v-col>
        <v-col cols="3" class="text-right">
          <v-btn color="primary" v-on:click="excelExport">
            To Excel
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="10">
          <scanner-status v-bind:catCodeString="this.catCodeString" />
        </v-col>
        <v-col cols="2">
          <div class="text--primary mt-5 mt-16 float-right">
            <div>Filters:</div>
            <div class="pl-4">
              <v-checkbox v-model="options.filter" label="US-only" class="ma-0" value="US" />
            </div>
          </div>
        </v-col>
      </v-row>

      <v-data-table dense :headers="headers" :options.sync="options" :must-sort=true @click:row="rowClicked"
        :items="items" :server-items-length="totalSnapshots" :footer-props="{
          itemsPerPageOptions: [-1]
        }" loading-text="Loading...please wait" :loading="loadingSnapshots">
        <template v-slot:item.scanEndDateTime="{ item }">
          {{ item.scanEndDateTime | toDateOnly('In Progress') }}
        </template>

        <template v-slot:item.refinedWeightedAvgPrice="{ item }">
          {{ item.refinedWeightedAvgPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.refinedMinPrice="{ item }">
          {{ item.refinedMinPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.qtyCapWeightedAvgPrice="{ item }">
          {{ item.qtyCapWeightedAvgPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.qtyCapMinPrice="{ item }">
          {{ item.qtyCapMinPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.activeWeightedAvgPrice="{ item }">
          {{ item.activeWeightedAvgPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.activeMinPrice="{ item }">
          {{ item.activeMinPrice | toCurrency(0) }}
        </template>
        <template v-slot:item.delete="{ item }">
          <v-icon small color="error" class="pa-1">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <ConfirmationDialog ref="confirm" />

  </v-container>
</template>

<style scoped>
table tr {
  cursor: pointer;
}
</style>

<script>
import { SnapshotsService } from '../../services/api/snapshots';
const apiSnapshots = new SnapshotsService();

import { MarketplacesService } from '../../services/api/marketplaces';
const apiMarketplaces = new MarketplacesService();

import ScannerStatus from '../../components/snapshots/scanner-status.vue';

// HACK:  import axios so we can send the user directly to the api to get Excel files.
import axios from 'axios';

export default {
  components: {
    ScannerStatus,
    ConfirmationDialog: () => import("../confirmation-dialog")
  },
  props: {
    catCodeString: String,
  },

  data() {

    return {
      scanning: false,
      scanStatus: "(unknown)",

      totalSnapshots: 0,
      items: [],
      marketplaces: [], // List of marketplaces to show in the UI.
      options: {
        sortDesc: [true], // Sort by descending date by default.
        timeframe: '3mo', // Default timeframe for the snapshots.
        filter: 'US', // Default to showing only US snapshots
      },
      loadingSnapshots: true,

      headers: [
        // HACK:  use ' d-none' (note the leading space) to hide the column.
        { text: 'ID', value: 'id', hidden: true, align: ' d-none' },
        { text: 'Marketplace', value: 'marketplace.name', sortable: false },
        { text: 'Date', value: 'scanEndDateTime' },

        { text: 'Refined Listings', value: 'refinedListingCount', align: 'end', divider: true },
        { text: 'Refined Qty', value: 'refinedTotalQty', align: 'end', divider: true },
        { text: 'Refined Avg $', value: 'refinedWeightedAvgPrice', align: 'end' },
        { text: 'Refined Min $', value: 'refinedMinPrice', align: 'end' },

        { text: 'QtyCap Listings', value: 'qtyCapListingCount', align: 'end' },
        { text: 'QtyCap Qty', value: 'qtyCapTotalQty', align: 'end' },
        { text: 'QtyCap Avg $', value: 'qtyCapWeightedAvgPrice', align: 'end' },
        { text: 'QtyCap Min $', value: 'qtyCapMinPrice', align: 'end' },

        { text: 'Active Listings', value: 'activeListingCount', align: 'end' },
        { text: 'Active Qty', value: 'activeTotalQty', align: 'end' },
        { text: 'Active Avg $', value: 'activeWeightedAvgPrice', align: 'end' },
        { text: 'Active Min $', value: 'activeMinPrice', align: 'end' },

        { text: '', value: 'delete', align: 'center', sortable: false }
      ],
    }
  },

  watch: {
    options: {
      handler() {
        this.getSnapshots();
      },
      deep: true,
    }
  },

  created() {
    // Listen to status changes coming from SignalR events
    this.$scannerHub.$on('scan-completed', this.onScanCompleted)

    // Get the list of marketplaces to show in the UI.
    apiMarketplaces.getMarketplaces().then((data) => {
      this.marketplaces = data;
    });
  },

  methods: {
    // This is called from the server through SignalR
    onScanCompleted({ newItemID }) {
      console.log("onScanCompleted; newSnapshotID=" + newItemID);
      apiSnapshots.clearCache();
      apiSnapshots.getSnapshot(newItemID).then((data) => {
        // Add the new Snapshot to the top of the list of items.
        this.items.unshift(data);
      });
    },

    getSnapshots() {
      this.loadingSnapshots = true;

      // Clear our list so the loading text will be shown.
      this.items = [];

      console.log(this.options);

      // Set up our search, sort, and paging parameters
      const search = {
        // If there's a sortBy field set, then use it; otherwise use a reasonable default.
        sortOrderField: this.options.sortBy.length == 1 ? this.options.sortBy[0] : "ScanEndDateTime",
        // If there's no sortDesc defined, sort by desc.  Otherwise sort by the value.
        sortDirection: this.options.sortDesc.length == 0 ? "desc" : (this.options.sortDesc[0] ? "desc" : "asc"),
        itemsPerPage: this.options.itemsPerPage,
        itemsToSkip: this.options.itemsPerPage * (this.options.page - 1),
        filter: this.options.filter || undefined, // Allow filtering to US-only.
      }

      // Get the list of items from the API.
      //apiSnapshots.getSnapshotsForPart(this.partID, search).then((data) => {
      apiSnapshots.getSnapshotsForCatCode(this.catCodeString, search).then((data) => {
        this.items = data.items
        this.totalSnapshots = data.totalItemCount;
      }).finally(() => {
        this.loadingSnapshots = false;
      });
    },

    async deleteItem(item) {
      // If the user confirms deletion, then:
      if (await this.$refs.confirm.open(
        "Confirm Delete",
        "Delete this Snapshot?",
        { okText: "Delete", okColor: "error" })) {

        // Delete the item and remove it from our collection.
        apiSnapshots.deleteSnapshot(item.id).then((data) => {
          // remove the item in question from our data.
          const index = this.items.indexOf(item);
          if (index > -1) {
            this.items.splice(index, 1);
          }
        });
      }
    },

    rowClicked(item) {
      // If the user clicked 'Delete', then:
      if (event.target.classList.contains("mdi-delete")) {
        // Delete that item.
        this.deleteItem(item);
      } else {
        // Else send the user to the item clicked.
        this.$router.push({ name: "Snapshot", params: { id: item.id } });
      }
    },

    // Export all snapshots to Excel.
    excelExport() {
      // direct the browser to an API controller that returns an Excel file with ALL snapshots for this CatCode
      window.open(axios.defaults.baseURL + 'api/snapshots/ExcelExport/history/' + this.catCodeString);
    }
  }
}
</script>